/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState, useRef } from 'react';
import { Building02 } from '../../../assets';
import { Store, CurrentUserResponse, Franchise, System } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../../theme/context/themeProviderContext';
import { Alert, Link, Box, Typography, Autocomplete, TextField, TextFieldProps } from '@mui/material';
import useLogError from '../../../hooks/useLogError';
import { transformForTranslation } from '../../orderly-for-distributors/ManageRoles/components/RolesBox';

const textClamp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};

const InitialStoreSelector = () => {
  const { getCurrentUser, getBrandingForFranchise } = useNswagClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [, changeBranding] = useContext(ThemeContext);
  const { t } = useTranslation('authentication');
  const {
    setUserContext,
    setSelectedStore,
    user,
    selectedSystem,
    selectedFranchise,
    setUserPermissions,
    setSystems,
    setFranchises,
    setStores,
    setSelectedFranchise,
    setSelectedSystem,
    forceLogout,
  } = useContext(UserContext);
  const { logError } = useLogError();
  const [loading, setLoading] = useState<boolean>(false);

  const [showSystemDropdown, setShowSystemDropdown] = useState<boolean>(true);
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState<boolean>(false);
  const isAutoSelectingRef = useRef<boolean>(false);

  const getAccessIssueMessage = (currentUser: CurrentUserResponse) => {
    const permissions = currentUser.permissions ?? [];
    const stores = (currentUser.stores as Store[]) ?? [];
    const issues: string[] = [];

    if (permissions.length === 0) {
      issues.push(t('errorMessages.noPermissionsAssigned'));
    }

    if (stores.length === 0) {
      issues.push(t('errorMessages.noStoresAssigned'));
    }

    return issues.length > 0 ? issues.join('\n\n') : null;
  };

  useEffect(() => {
    retrieveUser();
  }, []);

  const handleSingleStoreUser = (stores: Store[], franchises: Franchise[], systems: System[]) => {

    isAutoSelectingRef.current = true;

    if (systems.filter(s => s.id !== 0).length === 1) {
      const singleSystem = systems.filter(s => s.id !== 0)[0];
      setSelectedSystem(singleSystem);
      setShowSystemDropdown(false);

      const systemFranchises = franchises.filter(f =>
        f.systems?.some(s => s.id === singleSystem.id));

      if (systemFranchises.length === 1) {
        const singleFranchise = systemFranchises[0];
        setSelectedFranchise(singleFranchise);
        setShowFranchiseDropdown(false);

        const franchiseStores = stores.filter(s => s.franchiseId === singleFranchise.franchiseId);

        if (franchiseStores.length === 1) {
          const singleStore = franchiseStores[0];
          setSelectedStore({ ...singleStore, franchiseName: singleFranchise.franchiseName });
          setShowStoreDropdown(false);
          updateBranding(singleStore.franchiseId ?? 0);
        } else {
          isAutoSelectingRef.current = false;
          setShowStoreDropdown(true);
        }
      } else {
        isAutoSelectingRef.current = false;
        setShowFranchiseDropdown(true);
      }
    } else {
      isAutoSelectingRef.current = false;
      setShowSystemDropdown(true);
    }
  };

  const retrieveUser = () => {
    setLoading(true);
    getCurrentUser().then((result) => {
      if (result?.data) {
        const user: CurrentUserResponse = {
          ...result.data,
          stores: result.data.stores,
        };

        const accessIssueMessage = getAccessIssueMessage(user);

        setUserContext(user);
        setUserPermissions(result.data.permissions);
        setStores(result.data.stores as Store[]);
        setSystems(result.data.systems as System[]);
        setFranchises(result.data.franchises as Franchise[]);

        if (accessIssueMessage) {
          setErrorMessage(accessIssueMessage);
          return;
        }

        handleSingleStoreUser(result.data.stores as Store[], result.data.franchises as Franchise[], result.data.systems as System[]);
      } else {
        setErrorMessage(t('errorMessages.unableToLoadAccess'));
      }
    })
      .catch((error) => {
        setErrorMessage(t('errorMessages.unableToLoadAccess'));
        logError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeFranchise = (newValue: Franchise) => {
    if (newValue) {
      setSelectedFranchise(newValue);
      setSelectedStore(null);

      const franchiseStores = user?.stores?.filter(s => s.franchiseId === newValue.franchiseId) || [];
      if (franchiseStores.length === 1) {
        handleChangeStore(franchiseStores[0]);
        setShowStoreDropdown(false);
      } else {
        setShowStoreDropdown(true);
      }
    }
  };

  const handleChangeSystem = (newValue: System) => {
    if (newValue) {
      setSelectedSystem(newValue);
      setSelectedFranchise(null);
      setSelectedStore(null);

      const systemFranchises = user?.franchises?.filter(f =>
        f.systems?.some(s => s.id === newValue.id) &&
        user?.stores?.some(s => s.franchiseId === f.franchiseId)) || [];

      if (systemFranchises.length === 1) {
        handleChangeFranchise(systemFranchises[0]);
        setShowFranchiseDropdown(false);
      } else {
        setShowFranchiseDropdown(true);
        setShowStoreDropdown(false);
      }
    }
  };

  const handleChangeStore = (newValue: Store) => {
    if (newValue) {
      setSelectedStore({ ...newValue, franchiseName: selectedFranchise?.franchiseName ?? newValue.franchiseName });
      updateBranding(newValue.franchiseId ?? 0);
    }
  };

  useEffect(() => {
    if (isAutoSelectingRef.current) return;
    if (selectedSystem) {
      const systemFranchises = user?.franchises?.filter(f =>
        f.systems?.some(s => s.id === selectedSystem.id) &&
        user?.stores?.some(s => s.franchiseId === f.franchiseId)) || [];

      setShowFranchiseDropdown(systemFranchises.length > 0);

      if (systemFranchises.length === 1) {
        handleChangeFranchise(systemFranchises[0]);
      }
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (isAutoSelectingRef.current) return;
    if (selectedFranchise) {
      const franchiseStores = user?.stores?.filter(s => s.franchiseId === selectedFranchise.franchiseId) || [];
      setShowStoreDropdown(franchiseStores.length > 0);
      if (franchiseStores.length === 1) {
        handleChangeStore(franchiseStores[0]);
      }
    }
  }, [selectedFranchise]);

  const updateBranding = ((id: number) => {
    getBrandingForFranchise(id)
      .then((result) => {
        changeBranding(result?.data ? result.data : null);
      })
      .catch((error) => {
        console.log(error);
        changeBranding(null);
      }).finally(() => {
        isAutoSelectingRef.current = false;
      });
  });

  if (errorMessage) {
    return <Box
      sx={{
        position: 'absolute',
        top: '35%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '360px',
      }}>
      <Typography
        variant="displaySM"
        fontWeight={600}
        sx={{ textAlign: 'left' }}>
        {t('accessIssueTitle')}
      </Typography>
      <Typography
        color="textSecondary"
        variant="textMD"
        sx={{ mt: 2, textAlign: 'left' }}>
        {t('accessIssueDescription')}
      </Typography>
      <Alert severity="warning"
        sx={{ mt: 4, width: '100%', whiteSpace: 'pre-line' }}>{errorMessage}</Alert>
      <Link
        component="button"
        onClick={() => forceLogout()}
        sx={{
          alignSelf: 'flex-start',
          mb: 2,
          mt: 8,
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        ← {t('back')}
      </Link>
    </Box>;
  }

  return (
    <Box sx={{
      position: 'absolute',
      top: '35%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '360px',
    }}>
      <Link
        component="button"
        onClick={() => forceLogout()}
        sx={{
          alignSelf: 'flex-start',
          mb: 2,
          mt: 8,
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        ← {t('back')}
      </Link>
      {showSystemDropdown && (
        <Autocomplete
          onChange={(event: any, newValue: System | null) => {
            if (newValue)
              handleChangeSystem(newValue);
          }}
          id="select-system"
          //inputValue={t(transformForTranslation(selectedSystem?.name ?? ''))}
          options={user?.systems?.filter(s => s.id !== 0) ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: System) => option.name ?? ''}
          filterOptions={(options: System[], state) => options.filter((option) => option.name?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.id}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {t(transformForTranslation(option.name))}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectSystem')}
              {...params as TextFieldProps} />;
          }}
          value={selectedSystem}
        />
      )}

      {showFranchiseDropdown && (
        <Autocomplete
          onChange={(event: any, newValue: Franchise | null) => {
            if (newValue)
              handleChangeFranchise(newValue);
          }}
          id="select-franchise"
          options={selectedSystem
            ? (user?.franchises?.filter(franchise => {
              if (!franchise.systems || franchise.systems.length === 0) {
                return false;
              }

              if (!user?.stores ||
                user?.stores.length === 0 ||
                !user?.stores?.some(s => s.franchiseId === franchise.franchiseId || s.franchiseName?.toLocaleLowerCase() == franchise.franchiseName?.toLocaleLowerCase())) {
                return false;
              }

              return franchise.systems.some(system => {
                if (!system.id || !selectedSystem.id) return false;
                return system.id === selectedSystem.id;
              });
            }) ?? [])
            : []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Franchise) => option.franchiseName ?? ''}
          isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
          filterOptions={(options: Franchise[], state) => options.filter((option) => option.franchiseName?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.franchiseId}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.franchiseName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectFranchise')}
              {...params as TextFieldProps} />;
          }}
          value={selectedFranchise}
        />
      )}

      {showStoreDropdown && (
        <Autocomplete
          loading={loading}
          onChange={(event: any, newValue: Store | null) => {
            if (newValue)
              handleChangeStore(newValue);
          }}
          id="select-store"
          options={user?.stores?.filter(s => s.franchiseId == selectedFranchise?.franchiseId) ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Store) => option.storeName ?? ''}
          isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
          filterOptions={(options: Store[], state) => options.filter((option) => option.storeNumber?.startsWith(state.inputValue) || option.storeName?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.storeName}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.storeName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectStore')}
              {...params as TextFieldProps} />;
          }}
        />
      )}
    </Box>
  );
};

export default InitialStoreSelector;
